import { useEvents } from '@/api/events/events/getEvents';
import List from '@/components/Elements/List/List';
import AreaOpBadge from '@/components/Location/AreaOpBadge';
import { Heading2 } from '@/components/ui/typography/Typography';
import { LocationContext } from '@/providers/location';
import { dateFromApiDateTime, formatApiDate } from '@/utils/format';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import { useContext } from 'react';

const ONE_MINUTE = 1000 * 60;

const EventsInboxEmptyState = () => (
  <div
    data-testid="events-inbox-empty-state"
    className="m-3 flex flex-col items-center justify-center gap-1 rounded border border-dashed p-6 text-center text-foreground/50"
  >
    <ChatBubbleLeftRightIcon className="h-10 w-10" aria-hidden="true" />
    <p className="text-sm">No events are waiting on a text reply.</p>
  </div>
);

const EventsInboxSkeleton = () => (
  <div data-testid="events-inbox-skeleton" className="m-3 space-y-3">
    <div className="h-12 animate-pulse rounded bg-foreground/10" />
    <div className="h-12 animate-pulse rounded bg-foreground/10" />
  </div>
);

/**
 * Events whose customer texted last (the events `inbox` filter: waiting_since
 * set and not snoozed). Call-center agents run with the USA franchise (id 0)
 * active, which is what makes the API span every subscribed franchise instead
 * of a single location — the component just passes the active location
 * through, same as every other list.
 *
 * Unlike the queue, these are not assignments: nothing is claimed by opening
 * one, so rows link straight to the event's Messages tab where the reply
 * happens. Server order (longest-waiting handling is the server's call) is
 * rendered as-is. No realtime channel carries event texts, so a poll matching
 * the dashboard's cadence keeps the column honest.
 */
export const EventsInbox = () => {
  const { activeLocationId } = useContext(LocationContext);

  const { data: events, isPending } = useEvents({
    location: activeLocationId,
    search: { filter: 'inbox', include: 'franchise' },
    config: {
      refetchInterval: ONE_MINUTE,
      refetchOnWindowFocus: true,
    },
  });

  return (
    <div data-testid="events-inbox">
      <Heading2>Events awaiting text reply</Heading2>
      {isPending ? (
        <EventsInboxSkeleton />
      ) : !events?.length ? (
        <EventsInboxEmptyState />
      ) : (
        <List className="max-h-160 overflow-y-auto rounded border bg-background">
          {events.map((event) => (
            <List.ItemLink
              key={`events-inbox-${event.id}`}
              to={`/events/event/${event.id}/messages`}
              className="flex flex-col px-4 py-3"
            >
              <div className="space-x-2">
                <span>#{event.id}</span>
                <AreaOpBadge location={event.franchise} />
                <span>{event.name || 'No Name'}</span>
              </div>
              <span className="text-xs font-thin">Event {formatApiDate(event.start_at)}</span>
              {event.waiting_since && (
                <span className="text-xs font-medium text-red-600">
                  Waiting{' '}
                  {formatDistanceToNow(dateFromApiDateTime(event.waiting_since), {
                    addSuffix: true,
                  })}
                </span>
              )}
            </List.ItemLink>
          ))}
        </List>
      )}
    </div>
  );
};
